import React, {useState, useEffect} from 'react';
import './AccueilPage.css';
import type {Team} from './AccueilLayout';

const WEEK = [
    {date: 1, day: 'Lundi', short: 'Lun', isWeekend: false, isEvent: false},
    {date: 2, day: 'Mardi', short: 'Mar', isWeekend: false, isEvent: false},
    {date: 3, day: 'Mercredi', short: 'Mer', isWeekend: false, isEvent: false},
    {date: 4, day: 'Jeudi', short: 'Jeu', isWeekend: false, isEvent: false},
    {date: 5, day: 'Vendredi', short: 'Ven', isWeekend: false, isEvent: false},
    {date: 6, day: 'Samedi', short: 'Sam', isWeekend: true, isEvent: true},
    {date: 7, day: 'Dimanche', short: 'Dim', isWeekend: true, isEvent: false},
];

function PostIt({color, rotation, style, onClick, href, children}: {
    color?: string;
    rotation?: number;
    style?: React.CSSProperties;
    onClick?: () => void;
    href?: string;
    children: React.ReactNode;
}) {
    const rot = rotation ?? -2.5;
    const cls = `postit postit--${color || 'yellow'}${onClick || href ? ' postit--clickable' : ''}`;

    if (href) {
        return (
            <a
                className={cls}
                style={{transform: `rotate(${rot}deg)`, ...style}}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
            >
                {children}
            </a>
        );
    }

    return (
        <div
            className={cls}
            style={{transform: `rotate(${rot}deg)`, ...style}}
            onClick={onClick}
        >
            {children}
        </div>
    );
}

/* ─── Hand-drawn book icon ─── */
function BookIcon() {
    return (
        <svg width="1.4em" height="1.4em" viewBox="0 0 32 32" fill="none" stroke="currentColor"
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
             style={{verticalAlign: 'middle', marginRight: 6}}>
            <path d="M6 4c0-1 1-2 3-2h14c1.5 0 3 .8 3 2v24c0 1-1.5 2-3 2H9c-2 0-3-1-3-2V4z"/>
            <path d="M6 4c0 0 2 1 2 3v18c0 2-2 3-2 3"/>
            <path d="M12 10h8"/>
            <path d="M12 14h6"/>
            <path d="M12 18h7"/>
        </svg>
    );
}

/* ─── Journal intime modal ─── */
function JournalModal({onClose, team}: { onClose: () => void; team?: Team }) {
    const [showSuite, setShowSuite] = useState(false);

    return (
        <div className="journal-overlay" onClick={onClose}>
            <div className="journal-modal" onClick={e => e.stopPropagation()}>
                <button className="journal-modal__close" onClick={onClose}>&times;</button>
                <h2 className="journal-modal__title">Cher journal,</h2>
                <p className="journal-modal__text">
                    Heureusement que personne ne lit ce que j'écris… Maman, si t'es en train de lire, ça se fait pas,
                    t'arrêtes !
                </p>

                {!showSuite && (
                    <button className="journal-modal__reveal" onClick={() => setShowSuite(true)}>
                        <span className="journal-modal__arrow">&#x25BC;</span> Lire quand même
                    </button>
                )}

                {showSuite && (
                    <p className="journal-modal__text journal-modal__text--suite">
                        {team === 'juliette' ? (<>
                            Je m'appelle Juliette, j'ai 25 ans et j'ai un problème. Mais bon j'ai pas très envie d'en
                            parler…<br/>
                            Bon en fait mon problème c'est que Audrey a dit à Édouard que je l'aimais. Édouard
                            c'est pas que je l'aime, c'est juste qu'il a rigolé à une de mes blagues y a pas longtemps
                            donc j'ai dit à Audrey que je trouvais ça sympa de sa part.<br/>Bon en fait je l'aime mais
                            personne doit savoir.<br/>Sauf vous peut-être parce que vous êtes invités à notre PACS, mais
                            ça
                            c'est une autre histoire !
                        </>) : (<>
                            Je m'appelle Édouard, j'ai 26 ans et j'ai un problème. Mais bon j'ai pas très envie d'en
                            parler…<br/>
                            Bon en fait mon problème c'est que Matthieu a dit à la Grande Juliette que je l'aimais.
                            Juliette
                            c'est pas que je l'aime, c'est juste qu'elle a rigolé à une de mes blagues y a pas longtemps
                            donc j'ai dit à Matthieu que je trouvais ça sympa de sa part.<br/>Bon en fait je l'aime mais
                            personne doit savoir.<br/>Sauf vous peut-être parce que vous êtes invités à notre PACS, mais
                            ça
                            c'est une autre histoire !
                        </>)}
                    </p>
                )}
            </div>
        </div>
    );
}

/* ─── MSN notification popup (black & white) ─── */
function MsnNotification({visible, onClose, onClick, msnVisited, team}: {
    visible: boolean;
    onClose: () => void;
    onClick?: () => void;
    msnVisited?: boolean;
    team?: Team;
}) {
    const [vibrating, setVibrating] = useState(false);

    useEffect(() => {
        if (!visible || msnVisited) return;
        // Initial vibrate after 1s, then every 15s
        const firstTimer = setTimeout(() => setVibrating(true), 1000);
        const interval = setInterval(() => setVibrating(true), 3000);
        return () => {
            clearTimeout(firstTimer);
            clearInterval(interval);
        };
    }, [visible, msnVisited]);

    useEffect(() => {
        if (!vibrating) return;
        const t = setTimeout(() => setVibrating(false), 450);
        return () => clearTimeout(t);
    }, [vibrating]);

    return (
        <div
            className={`msn-notif${visible ? ' msn-notif--visible' : ''}${vibrating ? ' msn-notif--vibrate' : ''}`}
            onClick={onClick}
            style={onClick ? {cursor: 'pointer'} : undefined}
        >
            <button className="msn-notif__close" onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}>&times;</button>
            <div className="msn-notif__header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="2.5"
                     strokeLinecap="round" strokeLinejoin="round" style={{flexShrink: 0}}>
                    <circle cx="12" cy="8" r="5"/>
                    <path d="M3 21c0-4.5 4-8 9-8s9 3.5 9 8"/>
                </svg>
                <span className="msn-notif__sender">{team === 'juliette' ? 'Ed_le_BGÉ' : 'JuBu_du94'}</span>
                <span className="msn-notif__badge">MSN</span>
            </div>
            <p className="msn-notif__msg">salut! tu viens à ma boom le 6 juin??</p>
        </div>
    );
}

function NotesSection({onOpenJournal, msnVisible, onCloseMsn, onClickMsn, msnVisited, team}: {
    onOpenJournal: () => void;
    msnVisible: boolean;
    onCloseMsn: () => void;
    onClickMsn?: () => void;
    msnVisited?: boolean;
    team?: Team;
}) {
    return (
        <div className="notes-section">
            <h2 className="notes-title">Notes</h2>
            <div className="notes-content">
                <p className="notes-heading">Liste de choses qui vont<br/>s'améliorer avec le PACS :</p>
                <ol className="notes-list">
                    <li>Les impôts</li>
                    <li>L'héritage</li>
                    <li>Faire un prêt immo</li>
                    <li>Le status social</li>
                    <li><span className="notes-strikethrough">Faire un enfant</span></li>
                    <li>Les congés supplémentaires</li>
                    <li>Notre Amour</li>
                </ol>
            </div>

            {/* MSN notification above button */}
            <MsnNotification visible={msnVisible} onClose={onCloseMsn} onClick={onClickMsn} msnVisited={msnVisited}
                             team={team}/>

            <button className="journal-btn" onClick={onOpenJournal}>
                <BookIcon/>
                Journal intime
            </button>
        </div>
    );
}

const MAPS_URL = 'https://www.google.com/maps/dir/?api=1&destination=42+avenue+de+la+Dhuys,+Bagnolet';

export default function AccueilPage({onOpenModal, msnVisited, team}: {
    onOpenModal?: (id: string) => void;
    msnVisited?: boolean;
    team?: Team;
}) {
    const [journalOpen, setJournalOpen] = useState(false);
    const [msnVisible, setMsnVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setMsnVisible(true), 3000);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="agenda-wrapper">
            <div className="agenda">

                {/* ── Header ── */}
                <header className="agenda-header">
                    <span className="agenda-header__year">2025 — 2026</span>
                    <span className="agenda-header__week">
            Semaine du 1<sup>er</sup> au 7 juin 2026
          </span>
                    <span className="agenda-header__month">Juin</span>
                </header>

                {/* ── Day rows ── */}
                <div className="agenda-body">
                    {WEEK.map(({date, day, short, isWeekend, isEvent}) => (
                        <div
                            key={date}
                            className={[
                                'day-row',
                                isWeekend ? 'day-row--weekend' : '',
                                isEvent ? 'day-row--event' : '',
                            ].join(' ')}
                        >
                            <div className="day-label">
                                <span className="day-label__name">{day}</span>
                                <span className="day-label__short">{short}</span>
                                <span className={`day-label__num${isEvent ? ' day-label__num--circled' : ''}`}>
                                    {date}
                                    {isEvent && (
                                        <svg className="day-label__circle" viewBox="0 0 50 50"
                                             preserveAspectRatio="none">
                                            <ellipse
                                                cx="25" cy="25" rx="22" ry="20"
                                                fill="none" stroke="#e53935" strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeDasharray="120"
                                                strokeDashoffset="8"
                                            />
                                        </svg>
                                    )}
                                </span>
                            </div>
                            <div className="day-margin"/>
                            <div className="day-content"/>
                        </div>
                    ))}
                </div>

            </div>

            {/* ── Notes section ── */}
            <NotesSection
                onOpenJournal={() => setJournalOpen(true)}
                msnVisible={msnVisible}
                onCloseMsn={() => setMsnVisible(false)}
                onClickMsn={onOpenModal ? () => onOpenModal('msn') : undefined}
                msnVisited={msnVisited}
                team={team}
            />

            {/* ── Floating post-its (all same size) ── */}
            <PostIt color="yellow" rotation={-2.5} style={{top: '72%', left: '24.35%'}}>
                <p className="postit-line postit-line--main">PACS</p>
                <p className="postit-line">d'Édouard &amp; Juliette</p>
            </PostIt>

            <PostIt color="pink" rotation={3.5} style={{top: '28%', left: '66%'}}>
                <p className="postit-line postit-line--main">Horaire</p>
                <p className="postit-line">18h30</p>
                <p className="postit-line">&nbsp;</p>
            </PostIt>

            <PostIt color="blue" rotation={-1.8} style={{top: '48%', left: '44%'}} href={MAPS_URL}>
                <p className="postit-line postit-line--main">Le lieu</p>
                <p className="postit-line">42 avenue de la Dhuys</p>
                <p className="postit-line">à Bagnolet</p>
            </PostIt>

            <PostIt color="green" rotation={2} style={{top: '12%', left: '30%'}} href={MAPS_URL}>
                <p className="postit-line postit-line--main">Accès</p>
                <p className="postit-line">Métro Gallieni (L3)</p>
                <p className="postit-line">ou Tram T1 / Bus</p>
            </PostIt>

            {/* ── Journal modal ── */}
            {journalOpen && <JournalModal onClose={() => setJournalOpen(false)} team={team}/>}
        </div>
    );
}
